import React from "react";
import {
  BookOpenIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import { PageContainer } from "../components/PageContainer";
import {
  centeredPageHeaderClasses,
  heroPanelClasses,
  pageHeadingLargeClasses,
  pageLeadClasses,
  sectionCardClasses,
  sectionTitleClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import {
  primaryBtnModernClasses,
  secondaryBtnModernClasses,
} from "../styles/uiClasses";
import { aboutPageClasses } from "../styles/pageSpecificClasses";
import "../styles/heroBackgrounds.css";

const offerItems = [
  {
    icon: <BriefcaseIcon className="h-7 w-7 text-[#c96d3b]" />,
    title: "Extensive Job Listings",
    desc: "Browse verified summer roles in hospitality, tourism, retail, and more across Greece.",
  },
  {
    icon: <ClipboardDocumentListIcon className="h-7 w-7 text-[#34566a]" />,
    title: "Easy Applications",
    desc: "Apply with a streamlined flow that keeps momentum high for both candidates and employers.",
  },
  {
    icon: <CheckBadgeIcon className="h-7 w-7 text-[#d59d49]" />,
    title: "Verified Employers",
    desc: "Connect with reputable businesses offering dependable summer opportunities.",
  },
  {
    icon: <UsersIcon className="h-7 w-7 text-[#c96d3b]" />,
    title: "Community Support",
    desc: "Get practical tips, local knowledge, and help settling into seasonal life in Greece.",
  },
  {
    icon: <ShieldCheckIcon className="h-7 w-7 text-[#34566a]" />,
    title: "Secure and Reliable",
    desc: "Protecting candidate data and employer trust is part of the experience, not an afterthought.",
  },
  {
    icon: <BookOpenIcon className="h-7 w-7 text-[#d59d49]" />,
    title: "Guides for New Arrivals",
    desc: "Learn about culture, travel, and what to expect before you start your season.",
  },
];

const steps = [
  {
    icon: <MagnifyingGlassIcon className="mx-auto h-7 w-7 text-[#34566a]" />,
    title: "1. Discover Jobs",
    desc: "Explore opportunities from island resorts to city hospitality teams.",
  },
  {
    icon: <UserCircleIcon className="mx-auto h-7 w-7 text-[#34566a]" />,
    title: "2. Build Your Profile",
    desc: "Present your experience clearly so employers can spot a fit quickly.",
  },
  {
    icon: <CheckCircleIcon className="mx-auto h-7 w-7 text-[#34566a]" />,
    title: "3. Apply and Connect",
    desc: "Move from search to conversation without extra friction.",
  },
];

export default function About() {
  return (
    <PageContainer maxWidth="7xl">
      <section className={heroPanelClasses}>
        <div className={aboutPageClasses.heroGrid}>
          <div
            className={aboutPageClasses.heroImage}
            aria-hidden="true"
          />
          <div className={aboutPageClasses.heroCard}>
            <span className={subtleEyebrowClasses}>Who We Are</span>
            <h1 className={`mt-4 max-w-3xl ${pageHeadingLargeClasses}`}>
              Summer work in Greece, framed more like a destination than a generic listings site.
            </h1>
            <p className={`max-w-2xl ${pageLeadClasses}`}>
              We connect seasonal talent with trusted employers across Greece so the search feels inspiring, confident, and easy to navigate.
            </p>
            <div className={aboutPageClasses.featureGrid}>
              <div className={aboutPageClasses.featureCard}>
                <p className={aboutPageClasses.eyebrowWarm}>Travel-led</p>
                <p className={aboutPageClasses.bodySm}>Warm colors and postcard framing inspired by the setting.</p>
              </div>
              <div className={aboutPageClasses.featureCardAlt}>
                <p className={aboutPageClasses.eyebrowCool}>Practical</p>
                <p className={aboutPageClasses.bodySm}>Still structured enough for jobs, applications, and trust signals.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={centeredPageHeaderClasses}>
        <span className={subtleEyebrowClasses}>Our Mission</span>
        <div className={`${sectionCardClasses} ${aboutPageClasses.missionCard}`}>
          <p className={aboutPageClasses.missionCopy}>
            At <span className={aboutPageClasses.strong}>Summer Jobs In Greece</span>, our mission is to simplify finding and offering seasonal work in Greece. We help job seekers discover real opportunities and give employers a better way to reach motivated summer staff.
          </p>
        </div>
      </section>

      <section>
        <div className={aboutPageClasses.centerText}>
          <span className={subtleEyebrowClasses}>Platform Highlights</span>
          <h2 className={`mt-4 ${sectionTitleClasses}`}>What We Offer</h2>
        </div>
        <div className={aboutPageClasses.offerGrid}>
          {offerItems.map((item) => (
            <div key={item.title} className={`${sectionCardClasses} ${aboutPageClasses.offerCard}`}>
              <div className={aboutPageClasses.offerIcon}>{item.icon}</div>
              <h3 className={aboutPageClasses.offerTitle}>
                {item.title}
              </h3>
              <p className={aboutPageClasses.offerCopy}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className={aboutPageClasses.centerText}>
          <span className={subtleEyebrowClasses}>Simple Process</span>
          <h2 className={`mt-4 ${sectionTitleClasses}`}>How It Works</h2>
        </div>
        <div className={aboutPageClasses.stepsGrid}>
          {steps.map((step) => (
            <div key={step.title} className={`${sectionCardClasses} ${aboutPageClasses.stepCard}`}>
              {step.icon}
              <h3 className={aboutPageClasses.stepTitle}>
                {step.title}
              </h3>
              <p className={aboutPageClasses.offerCopy}>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={`${sectionCardClasses} ${aboutPageClasses.ctaSection}`}>
        <span className={subtleEyebrowClasses}>Ready When You Are</span>
        <h3 className={aboutPageClasses.ctaTitle}>
          Ready to start your Greek summer?
        </h3>
        <p className={aboutPageClasses.ctaCopy}>
          Browse live roles and contact employers directly from the public jobs board.
        </p>
        <div className={aboutPageClasses.ctaActions}>
          {/*
          Previous CTA when registration was public:
          <a href="/register" className={`${aboutPageClasses.ctaLink} ${primaryBtnModernClasses}`}>
            Get Started
          </a>
          */}
          <a href="/#jobs-section" className={`${aboutPageClasses.ctaLink} ${primaryBtnModernClasses}`}>
            Browse Jobs
          </a>
          <a href="/" className={`${aboutPageClasses.ctaLink} ${secondaryBtnModernClasses}`}>
            Explore Jobs
          </a>
        </div>
      </section>
    </PageContainer>
  );
}
